import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useSportContext } from "@/lib/useSportContext";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import TeamCheckoutDialog from "@/components/TeamCheckoutDialog";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Lock, Sparkles, Building2, ShieldCheck, Crown, Users, User2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Pricing page — Maturity-Tier Architecture
 * ─────────────────────────────────────────
 * Four tiers, none of which feature-gate the core intelligence engines
 * (ArmIQ · SpeedIQ · MotionIQ · RecoveryIQ). Upgrades unlock scale —
 * athlete seats, coach seats, multi-team, white-label, API.
 *
 * Athlete is the only tier with a monthly/annual toggle. Annual default,
 * "Save $79 annually" badge prominent.
 *
 * Team and Performance are annual only. Enterprise is consultation-only.
 *
 * Legacy SKUs (team_foundational, solo_annual, solo_monthly) remain
 * purchasable behind the existing /pricing/legacy route (kept in the
 * backend PACKAGES catalog) so grandfathered customers can renew.
 */

const ENGINES = ["ArmIQ", "SpeedIQ", "MotionIQ", "RecoveryIQ"];

export default function Pricing() {
  const { user } = useAuth();
  const sportCtx = useSportContext();
  const [billingCycle, setBillingCycle] = useState("annual"); // 'annual' | 'monthly' — Athlete tier only
  const [teamDialog, setTeamDialog] = useState(false);

  const startAthleteCheckout = () => {
    const pkg = billingCycle === "annual" ? "athlete_annual" : "athlete_monthly";
    window.location.href = `/solo?plan=${pkg}`;
  };

  const startTeamCheckout = (pkgId, extraSeats = 0) => {
    if (!user) {
      toast.info("Create your coach account first — checkout right after.");
      window.location.href = `/signup?intent=${pkgId}_purchase`;
      return;
    }
    if (user.role !== "coach" && user.role !== "admin") {
      toast.error("You're signed in as an athlete. Team plans cover a whole roster — log out and sign in with your coach account to purchase.", { duration: 8000 });
      return;
    }
    setTeamDialog({ pkg: pkgId, extra: extraSeats });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="pricing-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="md" variant="lockup" linkTo="/" dark />
          <nav className="flex items-center gap-5 text-sm text-white/70">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/login" className="hover:text-white">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[var(--ink-border)] px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-bold text-white/70">
          <Sparkles className="h-3 w-3" style={{ color: "var(--brand-secondary)" }} /> One Intelligence Platform · Four Maturity Tiers
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white mt-5 leading-[0.95]" data-testid="pricing-headline">
          {sportCtx.copy.headline}<br />
          <span style={{ color: "var(--brand-secondary)" }}>{sportCtx.copy.tagline}</span>
        </h1>
        {!sportCtx.isNeutral && (
          <div className="mt-3 text-[10px] tracking-[0.3em] uppercase font-bold text-fuchsia-300/80" data-testid="pricing-sport-tag">
            Pricing for {sportCtx.sport.name}
          </div>
        )}
        <p className="text-white/65 mt-5 max-w-2xl mx-auto leading-relaxed">
          Every tier ships with the full ArmIQ intelligence ecosystem — <span className="text-white font-semibold">ArmIQ, SpeedIQ, MotionIQ, and RecoveryIQ</span>. Upgrade to scale your roster, your coaching staff, and your organization — never to unlock features.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12 grid lg:grid-cols-4 md:grid-cols-2 gap-5">
        <AthleteCard cycle={billingCycle} setCycle={setBillingCycle} onBuy={startAthleteCheckout} />
        <TeamCard
          onBuy={(extra) => startTeamCheckout("team", extra)}
          onSwitchToPerformance={() => {
            const el = document.querySelector("[data-testid=pricing-card-performance]");
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.animate([
                { boxShadow: "0 0 0 0 rgba(217,4,41,0.0)" },
                { boxShadow: "0 0 0 6px rgba(217,4,41,0.35)" },
                { boxShadow: "0 0 0 0 rgba(217,4,41,0.0)" },
              ], { duration: 1400, iterations: 2 });
            }
          }}
        />
        <PerformanceCard onBuy={() => startTeamCheckout("performance")} />
        <EnterpriseCard />
      </section>

      {/* Universal-engine-access reassurance bar */}
      <section className="max-w-5xl mx-auto px-6 pb-20" data-testid="universal-engine-bar">
        <div className="rounded-2xl ink-card p-6 md:p-7">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">Universal intelligence access — every tier</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {ENGINES.map((e) => (
              <div key={e} className="flex items-center gap-2 rounded-lg border border-[var(--ink-border)] bg-[var(--ink-2)] px-3 py-2.5">
                <Check className="h-4 w-4" style={{ color: "var(--brand-secondary)" }} />
                <span className="text-sm font-semibold text-white">{e}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-white/55 mt-4 leading-relaxed">
            You will <span className="text-white font-semibold">never</span> see an &ldquo;Upgrade to unlock ArmIQ&rdquo; or &ldquo;Upgrade to unlock SpeedIQ&rdquo; message. Every customer — Athlete, Team, Performance, Enterprise — receives the complete intelligence ecosystem. Higher tiers unlock organizational scale, departmental collaboration, advanced integrations, and white-label branding.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-24 text-center">
        <div className="text-xs text-white/45 inline-flex items-center gap-2">
          <Lock className="h-3.5 w-3.5" /> Stripe-secured · all sales final per our
          <Link to="/refund-policy" className="underline hover:text-white">Refund Policy</Link>
        </div>
      </section>

      <TeamCheckoutDialog
        open={!!teamDialog}
        onOpenChange={(v) => setTeamDialog(v ? teamDialog : false)}
        packageId={teamDialog?.pkg}
        initialExtra={teamDialog?.extra || 0}
        foundationalRemaining={0}
      />
      <Footer />
    </div>
  );
}

/* ── Athlete card · monthly/annual toggle, Save $79 badge ───── */
function AthleteCard({ cycle, setCycle, onBuy }) {
  const price = cycle === "annual" ? "$149" : "$19";
  const period = cycle === "annual" ? "/ year" : "/ month";
  return (
    <div className="rounded-2xl ink-card p-6 flex flex-col" data-testid="pricing-card-athlete">
      <div className="inline-flex self-start items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] font-bold uppercase"
        style={{ backgroundColor: "rgba(34,211,238,0.18)", color: "#22D3EE" }}>
        <User2 className="h-3 w-3" /> Athlete
      </div>
      <div className="mt-3">
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Develop Yourself.</div>
        <h3 className="font-display font-black text-2xl tracking-tight text-white mt-1">Athlete</h3>
        <div className="flex items-baseline gap-1 mt-3">
          <span className="font-display font-black text-4xl text-white" data-testid="athlete-price">{price}</span>
          <span className="text-sm text-white/50">{period}</span>
        </div>
        <div className="mt-3 inline-flex p-0.5 rounded-full border border-[var(--ink-border)] bg-[var(--ink-2)]" data-testid="athlete-cycle-toggle">
          {[
            { v: "annual",  label: "Annual · Recommended" },
            { v: "monthly", label: "Monthly" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setCycle(o.v)}
              data-testid={`athlete-cycle-${o.v}`}
              className={`px-3 h-8 rounded-full text-xs font-bold transition ${cycle === o.v ? "text-white" : "text-white/50 hover:text-white"}`}
              style={cycle === o.v ? { backgroundColor: "#22D3EE", color: "#0A1424" } : {}}
            >
              {o.label}
            </button>
          ))}
        </div>
        {cycle === "annual" && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold border"
            style={{ borderColor: "rgba(16,185,129,0.5)", backgroundColor: "rgba(16,185,129,0.1)", color: "#10B981" }}
            data-testid="athlete-savings-badge">
            <ShieldCheck className="h-3 w-3" /> Save $79 annually
          </div>
        )}
      </div>
      <ul className="mt-5 space-y-2.5 flex-1">
        {[
          "Full ArmIQ · SpeedIQ · MotionIQ · RecoveryIQ access",
          "Personal athlete profile + readiness tracker",
          "Workload, recovery, AI insights (50 calls/mo)",
          "Apple Watch + Wear OS compatible · Hume Band recommended for full integration",
          "Mobile-first daily workflow",
          "Data persists across renewals",
        ].map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/75">
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#22D3EE" }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onBuy} className="mt-6 w-full text-[var(--ink-0)] font-bold rounded-full py-5" style={{ backgroundColor: "#22D3EE" }} data-testid="buy-athlete">
        Start as Athlete <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

/* ── Team card · $1,495/yr + $25/seat overflow past 40 ─────── */
function TeamCard({ onBuy, onSwitchToPerformance }) {
  const [extra, setExtra] = useState(0);
  const perfPrice = 2995;
  const total = 1495 + extra * 25;
  const roster = 40 + extra;
  const gapToPerf = perfPrice - total;                 // + when Team is still cheaper
  const nearPerf = gapToPerf <= 200 && gapToPerf > 0;  // within $200 → recommend
  const parity = total >= perfPrice;                    // Team ≥ Performance → auto-swap

  // Auto-swap to Performance the instant the two prices meet.
  useEffect(() => {
    if (parity && onSwitchToPerformance) {
      onSwitchToPerformance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parity]);

  return (
    <div className="rounded-2xl ink-card p-6 flex flex-col" data-testid="pricing-card-team">
      <div className="inline-flex self-start items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] font-bold uppercase"
        style={{ backgroundColor: "rgba(217,4,41,0.18)", color: "var(--brand-secondary)" }}>
        <Users className="h-3 w-3" /> Team
      </div>
      <div className="mt-3">
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Develop Your Roster.</div>
        <h3 className="font-display font-black text-2xl tracking-tight text-white mt-1">Team</h3>
        <div className="flex items-baseline gap-1 mt-3">
          <span className="font-display font-black text-4xl text-white tabular-nums" data-testid="team-total-price">${total.toLocaleString()}</span>
          <span className="text-sm text-white/50">/ year</span>
        </div>
        <div className="mt-1 text-xs text-white/50">
          {extra === 0
            ? "$1,495 base · up to 40 athletes"
            : <>Base $1,495 + <span className="font-mono-num">{extra}</span> extra × $25 · {roster} athletes total</>}
        </div>
        <div className="mt-3 text-[10px] tracking-[0.2em] font-bold uppercase text-white/40">Annual only</div>
      </div>

      <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="team-overflow-picker">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-bold text-white/45">
          <span>Roster size</span>
          <span className="text-white font-mono-num">{roster}</span>
        </div>
        <input
          type="range"
          min={0}
          max={60}
          step={1}
          value={extra}
          onChange={(e) => setExtra(Math.min(60, parseInt(e.target.value, 10) || 0))}
          className="w-full mt-2 accent-red-500"
          data-testid="team-overflow-slider"
        />
        <div className="mt-1 flex items-center justify-between text-[10px] text-white/40 font-mono-num">
          <span>40</span>
          <span>60</span>
          <span>80</span>
          <span>100</span>
        </div>
        <div className="mt-2 text-[11px] text-white/50">
          Extra seats bill at <b className="text-white">$25/athlete/yr</b>. Slider caps at 100 athletes — beyond that, Performance is the better plan.
        </div>
        {nearPerf && !parity && (
          <div className="mt-3 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 p-2.5 text-[11px] text-white"
               data-testid="team-recommend-performance">
            <b className="text-fuchsia-300">Recommendation:</b> at {roster} athletes you're only ${gapToPerf} from Performance ($2,995) —
            which includes unlimited athletes and every capability we ship. Consider switching.
            {onSwitchToPerformance && (
              <button
                type="button"
                onClick={onSwitchToPerformance}
                className="ml-2 underline text-fuchsia-300 font-semibold hover:text-white"
                data-testid="team-switch-to-performance"
              >
                Switch to Performance →
              </button>
            )}
          </div>
        )}
        {parity && (
          <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2.5 text-[11px] text-white"
               data-testid="team-auto-swapped-performance">
            <b className="text-emerald-300">Auto-upgraded.</b> At 100 athletes Team matches Performance — we've selected Performance for you (unlimited athletes, unlimited coaches, every engine).
          </div>
        )}
      </div>

      <ul className="mt-4 space-y-2.5 flex-1">
        {[
          "Up to 40 athlete profiles included",
          "+$25 / athlete / yr beyond 40 (up to 100 total)",
          "Up to 5 coach accounts",
          "1 team workspace",
          "Full intelligence ecosystem + 500 AI calls/mo",
          "Team readiness, compliance, position groups",
          "Standard wearable roster sync",
        ].map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/75">
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--brand-secondary)" }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button onClick={() => onBuy(extra)} className="mt-6 w-full text-white font-bold rounded-full py-5 red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="buy-team">
        Equip your team <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

/* ── Performance card · $2,995/yr ────────────────────────── */
function PerformanceCard({ onBuy }) {
  return (
    <div className="rounded-2xl p-6 flex flex-col border-2" style={{ borderColor: "var(--brand-secondary)", backgroundColor: "rgba(217,4,41,0.04)" }} data-testid="pricing-card-performance">
      <div className="inline-flex self-start items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] font-bold uppercase"
        style={{ backgroundColor: "var(--brand-secondary)", color: "#fff" }}>
        Most departments choose this
      </div>
      <div className="mt-3">
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Build a Performance Department.</div>
        <h3 className="font-display font-black text-2xl tracking-tight text-white mt-1">Performance</h3>
        <div className="flex items-baseline gap-1 mt-3">
          <span className="font-display font-black text-4xl text-white">$2,995</span>
          <span className="text-sm text-white/50">/ year</span>
        </div>
        <div className="mt-3 text-[10px] tracking-[0.2em] font-bold uppercase text-white/40">Annual only</div>
      </div>
      <ul className="mt-5 space-y-2.5 flex-1">
        {[
          "Unlimited athlete profiles",
          "Unlimited coach accounts",
          "Multiple team workspaces",
          "Departmental roles · head coach · position coaches · S&C · athletic trainer · sports medicine · analytics",
          "Unlimited AI insights · predictive risk modeling",
          "Advanced performance hardware integrations",
          "API access for custom pipelines",
        ].map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/85">
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--brand-secondary)" }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onBuy} className="mt-6 w-full text-white font-bold rounded-full py-5 red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="buy-performance">
        Build your department <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

/* ── Enterprise card · contact-only ─────────────────────── */
function EnterpriseCard() {
  return (
    <div className="rounded-2xl ink-card p-6 flex flex-col" data-testid="pricing-card-enterprise">
      <div className="inline-flex self-start items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] font-bold uppercase"
        style={{ backgroundColor: "rgba(255,215,0,0.18)", color: "#FFD700" }}>
        <Crown className="h-3 w-3" /> Enterprise
      </div>
      <div className="mt-3">
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Build an Organization.</div>
        <h3 className="font-display font-black text-2xl tracking-tight text-white mt-1">Enterprise</h3>
        <div className="flex items-baseline gap-1 mt-3">
          <span className="font-display font-black text-3xl text-white">Call for pricing</span>
        </div>
        <div className="mt-3 text-[10px] tracking-[0.2em] font-bold uppercase text-white/40">Consultation required</div>
      </div>
      <ul className="mt-5 space-y-2.5 flex-1">
        {[
          "Unlimited athletes, coaches, teams",
          "Multi-campus organizational hierarchy",
          "Executive dashboards + organization benchmarking",
          "Custom AI models · enterprise data pipelines",
          "Full API suite + webhooks + dedicated rate limits",
          "White-labeling, custom branding, custom domains",
          "Dedicated success engineer",
        ].map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/75">
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#FFD700" }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Link to="/enterprise/contact" data-testid="buy-enterprise">
        <Button className="mt-6 w-full text-[var(--ink-0)] font-bold rounded-full py-5" style={{ backgroundColor: "#FFD700" }}>
          <Building2 className="h-4 w-4 mr-2" /> Book a consultation
        </Button>
      </Link>
    </div>
  );
}
